import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';

type Faq = {
  id: number;
  category: string;
  question: string;
  content: string;
  sortOrder: number;
  isPublished: 0 | 1;
  linkLabel: string;
  linkUrl: string;
  createdAt: string;
  updatedAt: string;
};

type ApiEnvelope<T> = { ok: boolean; data?: T; message?: string };

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

// 공개 페이지의 분류 버튼은 등록된 질문의 카테고리로 만들어진다. 자주 쓰는 이름을 추천으로 보여 준다.
const CATEGORY_SUGGESTIONS = ['서비스', '요금 & 결제', '계약 & 보안', '회사 소개', '기술 지원'];

const EMPTY_FORM = {
  category: '서비스',
  question: '',
  content: '',
  sortOrder: 0,
  isPublished: true,
  linkLabel: '',
  linkUrl: '',
};

export default function FaqArchive() {
  const session = getAdminSession();
  const [items, setItems] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<Faq[]>>(`${MainURL}/admin-console/faqs`);
      if (res.data?.ok && res.data.data) {
        setItems(res.data.data);
      } else {
        setErrorMsg(res.data?.message || 'FAQ 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      setErrorMsg(errorMessageOf(err, 'FAQ 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  const startEdit = (faq: Faq) => {
    setEditingId(faq.id);
    setForm({
      category: faq.category,
      question: faq.question,
      content: faq.content,
      sortOrder: faq.sortOrder,
      isPublished: Boolean(faq.isPublished),
      linkLabel: faq.linkLabel || '',
      linkUrl: faq.linkUrl || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session) return;
    if (!form.question.trim() || !form.content.trim()) {
      alert('질문과 답변을 입력해 주세요.');
      return;
    }
    if (Boolean(form.linkLabel.trim()) !== Boolean(form.linkUrl.trim())) {
      alert('링크 문구와 주소를 함께 입력해 주세요.');
      return;
    }
    setSaving(true);
    try {
      const payload = { requesterId: session.id, ...form };
      const res = editingId
        ? await axios.put<ApiEnvelope<null>>(`${MainURL}/admin-console/faqs/${editingId}`, payload)
        : await axios.post<ApiEnvelope<null>>(`${MainURL}/admin-console/faqs`, payload);
      if (!res.data?.ok) throw new Error(res.data?.message);
      cancelEdit();
      fetchFaqs();
    } catch (err) {
      alert(errorMessageOf(err, '저장하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!session) return;
    if (!window.confirm('이 질문을 삭제하시겠습니까?')) return;
    try {
      const res = await axios.delete<ApiEnvelope<null>>(`${MainURL}/admin-console/faqs/${id}`, {
        params: { requesterId: session.id },
      });
      if (res.data?.ok) {
        setItems((prev) => prev.filter((item) => item.id !== id));
      } else {
        alert(res.data?.message || '삭제하지 못했습니다.');
      }
    } catch (err) {
      alert(errorMessageOf(err, '삭제하지 못했습니다.'));
    }
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">자주 묻는 질문 관리</h2>
          <p className="admin-panel-desc">
            회사소개 &gt; 자주 묻는 질문 페이지(/company/faq)에 보일 질문을 등록·수정합니다. 공개한 질문만
            보이며, 순서 숫자가 작은 질문이 먼저 나옵니다.
          </p>
        </div>
        <button type="button" className="admin-refresh-btn" onClick={fetchFaqs} disabled={loading}>
          {loading ? '불러오는 중...' : '새로고침'}
        </button>
      </div>

      <form className="admin-form" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="카테고리"
          list="faq-category-suggestions"
          value={form.category}
          onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
          style={{ maxWidth: 160 }}
        />
        <datalist id="faq-category-suggestions">
          {CATEGORY_SUGGESTIONS.map((cat) => (
            <option key={cat} value={cat} />
          ))}
        </datalist>
        <input
          type="text"
          placeholder="질문"
          value={form.question}
          onChange={(e) => setForm((prev) => ({ ...prev, question: e.target.value }))}
        />
        <textarea
          placeholder="답변"
          value={form.content}
          onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
          rows={5}
        />
        <input
          type="text"
          placeholder="링크 문구 (선택, 예: 요금 페이지 보기 →)"
          value={form.linkLabel}
          onChange={(e) => setForm((prev) => ({ ...prev, linkLabel: e.target.value }))}
        />
        <input
          type="text"
          placeholder="링크 주소 (선택, 예: /price)"
          value={form.linkUrl}
          onChange={(e) => setForm((prev) => ({ ...prev, linkUrl: e.target.value }))}
        />
        <input
          type="number"
          placeholder="순서"
          title="순서 (작을수록 먼저)"
          value={form.sortOrder}
          onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: Number(e.target.value) || 0 }))}
          style={{ maxWidth: 100 }}
        />
        <label className="admin-form-checkbox">
          <input
            type="checkbox"
            checked={form.isPublished}
            onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))}
          />
          공개 (자주 묻는 질문 페이지에 표시)
        </label>
        <div className="admin-form-actions">
          <button type="submit" className="admin-refresh-btn" disabled={saving}>
            {editingId ? '수정 저장' : '질문 등록'}
          </button>
          {editingId && (
            <button type="button" className="admin-cancel-btn" onClick={cancelEdit}>
              취소
            </button>
          )}
        </div>
      </form>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>순서</th>
              <th>카테고리</th>
              <th>질문</th>
              <th>답변</th>
              <th>상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={6}>등록된 질문이 없습니다.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{item.sortOrder}</td>
                  <td>{item.category}</td>
                  <td style={{ textAlign: 'left' }}>{item.question}</td>
                  <td style={{ maxWidth: 320, whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                    {item.content}
                    {item.linkUrl ? `\n🔗 ${item.linkLabel} (${item.linkUrl})` : ''}
                  </td>
                  <td>
                    <span className={`admin-status-tag admin-status-tag--${item.isPublished ? 'signed' : 'pending'}`}>
                      {item.isPublished ? '공개' : '비공개'}
                    </span>
                  </td>
                  <td>
                    <button type="button" className="admin-btn edit-btn" onClick={() => startEdit(item)}>
                      수정
                    </button>
                    <button type="button" className="admin-btn delete-btn" onClick={() => handleDelete(item.id)}>
                      삭제
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
